import Vue from 'vue';
import VueMeta from 'vue-meta';
import VueRouter from 'vue-router';
import multiguard from 'vue-router-multiguard';
import guards from './guards';

// Views
import Admin from '@/components/views/Admin';
import Analytics from '@/components/views/Analytics';
import Billing from '@/components/views/Billing';
import Blocks from '@/components/views/Blocks';
import EditBlock from '@/components/views/EditBlock';
import EditPage from '@/components/views/EditPage';
import EditProfile from '@/components/views/EditProfile';
import EditRevision from '@/components/views/EditRevision';
import EditSite from '@/components/views/EditSite';
import EditSource from '@/components/views/EditSource';
import EditTemplate from '@/components/views/EditTemplate';
import EditUser from '@/components/views/EditUser';
import Error from '@/components/views/Error';
import Home from '@/components/views/Home';
import Invoice from '@/components/views/Invoice';
import Login from '@/components/views/Login';
import Logout from '@/components/views/Logout';
import NewBlock from '@/components/views/NewBlock';
import NewPage from '@/components/views/NewPage';
import NewSite from '@/components/views/NewSite';
import NewTemplate from '@/components/views/NewTemplate';
import NewUser from '@/components/views/NewUser';
import RecoverPassword from '@/components/views/RecoverPassword';
import ResetPassword from '@/components/views/ResetPassword';
import Settings from '@/components/views/Settings';
import Site from '@/components/views/Site';
import Sites from '@/components/views/Sites';
import Support from '@/components/views/Support';
import Templates from '@/components/views/Templates';
import User from '@/components/views/User';
import Users from '@/components/views/Users';
import Welcome from '@/components/views/Welcome';

Vue.use(VueMeta);
Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  linkActiveClass: 'is-current',
  linkExactActiveClass: 'is-current-exact',
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else if (to.hash) {
      return { selector: to.hash };
    } else {
      return { x: 0, y: 0 };
    }
  },
  routes: [
    // Home
    {
      path: '/',
      name: 'Home',
      component: Home,
      beforeEnter: guards.mustBeAuthenticated
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Auth
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Welcome
    {
      path: '/welcome',
      name: 'Welcome',
      component: Welcome,
      beforeEnter: guards.mustNotBeAuthenticated
    },

    // Login
    {
      path: '/login',
      name: 'Login',
      component: Login,
      beforeEnter: guards.mustNotBeAuthenticated
    },

    // Logout
    {
      path: '/logout',
      name: 'Logout',
      component: Logout
    },

    // Recover password
    {
      path: '/login/recover',
      name: 'RecoverPassword',
      component: RecoverPassword,
      beforeEnter: guards.mustNotBeAuthenticated
    },

    // Reset password
    {
      path: '/login/reset',
      name: 'ResetPassword',
      component: ResetPassword,
      beforeEnter: guards.mustNotBeAuthenticated
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Admin
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Sites
    {
      path: '/admin',
      name: 'Admin',
      component: Admin,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeSuperuser])
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Sites
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Sites
    {
      path: '/sites',
      name: 'Sites',
      component: Sites,
      beforeEnter: guards.mustBeAuthenticated
    },

    // New site
    {
      path: '/sites/new',
      name: 'NewSite',
      component: NewSite,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // Site
    {
      path: '/sites/:siteId',
      name: 'Site',
      component: Site,
      beforeEnter: guards.mustBeAuthenticated
    },

    // Edit site
    {
      path: '/sites/:siteId/edit',
      name: 'EditSite',
      component: EditSite,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Templates
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Templates
    {
      path: '/sites/:siteId/templates',
      name: 'Templates',
      component: Templates,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // New template
    {
      path: '/sites/:siteId/templates/new',
      name: 'NewTemplate',
      component: NewTemplate,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // Edit template
    {
      path: '/sites/:siteId/templates/:templateId/edit',
      name: 'EditTemplate',
      component: EditTemplate,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Blocks
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Blocks
    {
      path: '/sites/:siteId/blocks',
      name: 'Blocks',
      component: Blocks,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // New block
    {
      path: '/sites/:siteId/blocks/new',
      name: 'NewBlock',
      component: NewBlock,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // Edit block
    {
      path: '/sites/:siteId/blocks/:blockId/edit',
      name: 'EditBlock',
      component: EditBlock,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Pages
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // New page
    {
      path: '/sites/:siteId/pages/new',
      name: 'NewPage',
      component: NewPage,
      beforeEnter: guards.mustBeAuthenticated
    },

    // Edit page
    {
      path: '/sites/:siteId/pages/:pageId',
      name: 'EditPage',
      component: EditPage,
      beforeEnter: guards.mustBeAuthenticated
    },

    // Edit revision
    {
      path: '/sites/:siteId/pages/:pageId/revisions/:revisionId',
      name: 'EditRevision',
      component: EditRevision,
      beforeEnter: guards.mustBeAuthenticated
    },

    // Edit source
    {
      path: '/sites/:siteId/pages/:pageId/source',
      name: 'EditSource',
      component: EditSource,
      beforeEnter: guards.mustBeAuthenticated
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Analytics
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Analytics
    {
      path: '/sites/:siteId/analytics',
      name: 'Analytics',
      component: Analytics,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeBusiness])
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Users
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Users
    {
      path: '/users',
      name: 'Users',
      component: Users,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // New user
    {
      path: '/users/new',
      name: 'NewUser',
      component: NewUser,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // User
    {
      path: '/users/:userId',
      name: 'User',
      component: User,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // Edit user
    {
      path: '/users/:userId/edit',
      name: 'EditUser',
      component: EditUser,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.mustBeAdmin])
    },

    // Edit Profile
    {
      path: '/profile',
      name: 'EditProfile',
      component: EditProfile,
      beforeEnter: guards.mustBeAuthenticated
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Support
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Support
    {
      path: '/support',
      name: 'Support',
      component: Support,
      beforeEnter: guards.mustBeAuthenticated
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Settings
    ////////////////////////////////////////////////////////////////////////////////////////////////

    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.canManageSettings])
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Billing
    ////////////////////////////////////////////////////////////////////////////////////////////////

    {
      path: '/billing',
      name: 'Billing',
      component: Billing,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.canManageBilling])
    },

    {
      path: '/billing/invoices/:paymentId',
      name: 'Invoice',
      component: Invoice,
      beforeEnter: multiguard([guards.mustBeAuthenticated, guards.canManageBilling])
    },

    ////////////////////////////////////////////////////////////////////////////////////////////////
    // Error
    ////////////////////////////////////////////////////////////////////////////////////////////////

    // Not Found
    {
      path: '*',
      name: 'NotFound',
      component: Error,
      props: {
        statusCode: 404
      }
    }
  ]
});

export default router;
